/**
 * workItemsApi.ts
 * API クライアント — 実施業務 (case_work_items)
 */
import { getAuthToken } from "../lib/clerkAuth.js";

const DEV_USER_ID = "user_amano_akihiro";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-User-Id": DEV_USER_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ── 型定義 ────────────────────────────────────────────────────────────────

export type WorkItem = {
  id:                string;
  profession:        string;
  category:          string;
  title:             string;
  description:       string | null;
  quantity:          number;
  unit_price:        number;
  amount:            number;
  tax_category:      string;
  withholding_target: boolean;
  is_pass_through:   boolean;
  target_doc_types:  string[] | null;
  source:            string;
  sort_order:        number;
  created_by:        string;
  created_at:        string;
  updated_at:        string;
};

export type WorkItemsSummary = {
  by_profession: Record<string, {
    fee_subtotal: number;
    pass_through: number;
    item_count:   number;
  }>;
  totals: {
    fee_subtotal:    number;
    pass_through:    number;
    consumption_tax: number;
    withholding_tax: number;
    grand_total:     number;
  };
};

export type GenerateRequest = {
  doc_types:       string[];
  mode:            "create" | "replace_draft" | "new_version";
  work_item_ids?:  string[];
  idempotency_key?: string;
  dry_run?:        boolean;
};

export type GenerationResult = {
  generated: Array<{
    doc_type:             string;
    document_id:          string;
    status:               "draft";
    version:              number;
    from_mode:            string;
    source_work_item_ids: string[];
    totals: {
      fee_subtotal:    number;
      tax_subtotal:    number;
      consumption_tax: number;
      withholding_tax: number;
      total:           number;
    };
  }>;
  skipped:  Array<{ doc_type: string; reason: string }>;
  warnings: Array<{ code: string; message: string }>;
};

export type WorkItemInput = {
  profession:         string;
  category:           string;
  title:              string;
  description?:       string;
  quantity?:          number;
  unit_price?:        number;
  amount?:            number;
  tax_category?:      string;
  withholding_target?: boolean;
  is_pass_through?:   boolean;
  target_doc_types?:  string[] | null;
  sort_order?:        number;
};

// ── API 関数 ──────────────────────────────────────────────────────────────

export const workItemsApi = {
  list: (caseId: string, profession?: string) => {
    const q = profession ? `?profession=${profession}` : "";
    return request<{ data: WorkItem[]; total: number }>(`/cases/${caseId}/work-items${q}`);
  },
  create: (caseId: string, body: WorkItemInput) =>
    request<WorkItem>(`/cases/${caseId}/work-items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  bulkCreate: (caseId: string, items: WorkItemInput[]) =>
    request<{ data: WorkItem[]; total: number }>(`/cases/${caseId}/work-items?bulk=1`, {
      method: "POST",
      body: JSON.stringify({ items }),
    }),
  update: (caseId: string, itemId: string, body: Partial<WorkItemInput>) =>
    request<WorkItem>(`/cases/${caseId}/work-items/${itemId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  delete: (caseId: string, itemId: string) =>
    request<{ ok: boolean }>(`/cases/${caseId}/work-items/${itemId}`, {
      method: "DELETE",
    }),
  reorder: (caseId: string, items: Array<{ id: string; sort_order: number }>) =>
    request<{ ok: boolean }>(`/cases/${caseId}/work-items/reorder`, {
      method: "POST",
      body: JSON.stringify({ items }),
    }),
  summary: (caseId: string) =>
    request<WorkItemsSummary>(`/cases/${caseId}/work-items/summary`),
  generate: (caseId: string, body: GenerateRequest) =>
    request<GenerationResult>(`/cases/${caseId}/documents/generate-from-work-items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
